import { useEffect, useState } from "react";
import { Calendar, LogOut, Moon, Save, Sun, Users } from "lucide-react";

import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/bits";
import { DatePicker, Field, INPUT, PrimaryBtn, Segmented, Select } from "../components/ui";
import SegmentEditor from "../components/SegmentEditor";
import { FREQUENCIES, scheduleCount } from "../lib/format";

export default function SettingsPage() {
  const { settings, saveSettings, dark, setDark, showToast } = useStore();
  const { user, logout } = useAuth();
  const [local, setLocal] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => setLocal(settings), [settings]);

  const upd = (k, v) => setLocal((s) => ({ ...s, [k]: v }));

  const dirty =
    (local.org_name || "") !== (settings.org_name || "") ||
    (local.start_date || "") !== (settings.start_date || "") ||
    (local.end_date || "") !== (settings.end_date || "") ||
    (local.frequency || "weekly") !== (settings.frequency || "weekly") ||
    JSON.stringify(local.audiences || []) !== JSON.stringify(settings.audiences || []);

  const save = async () => {
    setSaving(true);
    try {
      await saveSettings({
        org_name: local.org_name || "",
        start_date: local.start_date || "",
        end_date: local.end_date || "",
        frequency: local.frequency || "weekly",
        audiences: local.audiences || [],
      });
      showToast("Settings saved");
    } catch {
      showToast("Could not save settings");
    }
    setSaving(false);
  };

  const perPlatform = scheduleCount(local.start_date, local.end_date, local.frequency || "weekly");

  return (
    <div className="animate-fade-in max-w-2xl">
      <PageHeader
        title="Settings"
        subtitle="How often to post and over what dates. These drive the calendar."
        right={
          <PrimaryBtn onClick={save} disabled={!dirty || saving}>
            <Save size={16} />
            Save
          </PrimaryBtn>
        }
      />

      <div className="space-y-5">
        <div className="bg-surface border border-border rounded-2xl p-5">
          <Field
            label="Brand or creator name"
            hint="optional, gives the AI context"
            info="The name shown on posts. The handle under it is the username you set per account on the Accounts page."
          >
            <input
              className={INPUT}
              placeholder="Leave blank if you only post as individuals"
              value={local.org_name || ""}
              onChange={(e) => upd("org_name", e.target.value)}
            />
          </Field>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-foreground">
            <Users size={17} className="text-muted" />
            <div className="font-medium text-[15px]">Target audiences</div>
          </div>
          <p className="text-[13px] text-muted leading-relaxed">
            Define the segments you create content for. Give each a short name and a description of who they are,
            their situation and their demographics. When you generate, you can focus a batch on one or more of these.
          </p>
          <SegmentEditor
            items={local.audiences || []}
            onChange={(v) => upd("audiences", v)}
            labelPlaceholder="Name, e.g. Job seekers"
            descPlaceholder="Who they are, their situation and demographics, e.g. Recent graduates in Nigeria, 22 to 35, applying for remote roles."
            addLabel="Add audience"
          />
        </div>

        <div className="bg-surface border border-border rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-foreground">
            <Calendar size={17} className="text-muted" />
            <div className="font-medium text-[15px]">Schedule</div>
          </div>

          <Field label="Frequency">
            <Select
              value={local.frequency || "weekly"}
              onChange={(v) => upd("frequency", v)}
              options={FREQUENCIES}
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Start date">
              <DatePicker
                value={local.start_date || ""}
                onChange={(v) => upd("start_date", v)}
                placeholder="Today"
              />
            </Field>
            <Field label="End date">
              <DatePicker
                value={local.end_date || ""}
                onChange={(v) => upd("end_date", v)}
                placeholder="4 weeks out"
              />
            </Field>
          </div>

          <div className="text-[13px] text-muted bg-foreground/[0.03] rounded-xl px-3.5 py-2.5">
            This creates about <span className="font-semibold text-foreground">{perPlatform}</span>{" "}
            post{perPlatform === 1 ? "" : "s"} per connected platform, spaced across the range.
            {perPlatform >= 8 ? " Capped at 8 per platform per run." : ""}
          </div>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {dark ? <Moon size={18} className="text-muted" /> : <Sun size={18} className="text-muted" />}
            <div>
              <div className="font-medium text-foreground text-[15px]">Appearance</div>
              <div className="text-[12.5px] text-faint">{dark ? "Dark" : "Light"} theme</div>
            </div>
          </div>
          <Segmented
            value={dark ? "dark" : "light"}
            onChange={(v) => setDark(v === "dark")}
            options={[
              { value: "light", label: "Light", icon: <Sun size={14} /> },
              { value: "dark", label: "Dark", icon: <Moon size={14} /> },
            ]}
          />
        </div>

        <div className="bg-surface border border-border rounded-2xl p-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="font-medium text-foreground text-[15px]">Account</div>
            <div className="text-[12.5px] text-faint truncate">
              {user ? user.email : "Signed in"}
            </div>
          </div>
          <button
            onClick={logout}
            className="shrink-0 inline-flex items-center gap-2 border border-border rounded-xl px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-foreground/5 active:scale-[0.97]"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}